import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowLeftIcon, CreditCardIcon, BuildingLibraryIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { getPortalInvoice, createPortalCheckout, createPortalAchIntent, getPortalPaymentConfig } from '../../services/portalService';
import Badge from '../../components/common/Badge';
import Spinner from '../../components/common/Spinner';

const badgeVariant = (status) => {
  if (status === 'paid') return 'success';
  if (status === 'overdue') return 'error';
  if (status === 'cancelled') return 'inactive';
  return 'default';
};

const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

export default function PortalInvoiceDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [paying, setPaying] = useState(false);
  const [achState, setAchState] = useState('idle'); // idle | collecting | processing | done | error

  const paymentResult = searchParams.get('payment'); // 'success' | 'cancelled'

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['portal', 'invoice', id],
    queryFn: () => getPortalInvoice(id),
    refetchOnWindowFocus: paymentResult === 'success',
  });

  const { data: paymentConfig } = useQuery({
    queryKey: ['portal', 'payment-config'],
    queryFn: getPortalPaymentConfig,
    staleTime: 5 * 60 * 1000,
  });

  const surchargePercent = paymentConfig?.cardSurchargePercent ?? 0;

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const url = await createPortalCheckout(id);
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start payment session');
      setPaying(false);
    }
  };

  const handleAchPay = async () => {
    if (!stripePromise) {
      toast.error('Online payments are not configured');
      return;
    }

    setAchState('collecting');
    try {
      const { clientSecret } = await createPortalAchIntent(id);
      const stripe = await stripePromise;

      // Opens the Stripe Financial Connections modal for bank account linking
      const { paymentIntent, error } = await stripe.collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: invoice.client?.firstName + ' ' + invoice.client?.lastName },
          },
        },
        expand: ['payment_method'],
      });

      if (error) {
        setAchState('error');
        toast.error(error.message || 'Bank account collection failed');
        return;
      }

      if (paymentIntent.status === 'requires_payment_method') {
        // User closed the modal without linking
        setAchState('idle');
        return;
      }

      // Confirm the payment (client accepts mandate)
      setAchState('processing');
      const { error: confirmError } = await stripe.confirmUsBankAccountPayment(clientSecret);
      if (confirmError) {
        setAchState('error');
        toast.error(confirmError.message || 'Failed to confirm bank payment');
        return;
      }

      setAchState('done');
      queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
      toast.success('Bank payment initiated — ACH transfers take 1–3 business days to clear.');
    } catch (err) {
      setAchState('error');
      toast.error(err.response?.data?.message || 'ACH payment failed');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  if (!invoice) {
    return <p className="text-neutral-600 dark:text-neutral-400">Invoice not found.</p>;
  }

  const balance = parseFloat(invoice.total) - parseFloat(invoice.amountPaid);
  const canPay = balance > 0 && !['cancelled', 'paid'].includes(invoice.status);
  const stripeEnabled = Boolean(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

  return (
    <div className="space-y-6">
      {/* Payment result banners */}
      {paymentResult === 'success' && (
        <div className="flex items-start gap-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 text-sm text-green-800 dark:text-green-300">
          <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Payment received — thank you!</p>
            <p className="text-green-700 dark:text-green-400 mt-0.5">
              Your payment is being processed. The invoice status will update shortly.
            </p>
          </div>
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div className="flex items-start gap-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 text-sm text-yellow-800 dark:text-yellow-300">
          <XCircleIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 shrink-0 mt-0.5" />
          <p>Payment was cancelled. You can try again below.</p>
        </div>
      )}
      {achState === 'done' && (
        <div className="flex items-start gap-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 text-sm text-green-800 dark:text-green-300">
          <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Bank payment initiated</p>
            <p className="text-green-700 dark:text-green-400 mt-0.5">
              ACH transfers take 1–3 business days to clear. Your invoice will update automatically.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Link to="/portal/invoices" className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{invoice.invoiceNumber}</h1>
        <Badge label={invoice.status} variant={badgeVariant(invoice.status)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <div className="card">
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Invoice date</p>
          <p className="mt-1 font-medium text-neutral-900 dark:text-neutral-100">{format(new Date(invoice.invoiceDate), 'MMM d, yyyy')}</p>
        </div>
        <div className="card">
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Due date</p>
          <p className="mt-1 font-medium text-neutral-900 dark:text-neutral-100">{format(new Date(invoice.dueDate), 'MMM d, yyyy')}</p>
        </div>
        <div className="card">
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Balance due</p>
          <p className={`mt-1 font-bold text-lg ${balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
            ${balance.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Payment options */}
      {canPay && stripeEnabled && achState !== 'done' && (
        <div className="card space-y-4">
          <div>
            <p className="font-semibold text-neutral-900 dark:text-neutral-100">Pay ${balance.toFixed(2)}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Choose your preferred payment method.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Card via Stripe Checkout */}
            <button
              onClick={handlePayNow}
              disabled={paying || achState === 'collecting' || achState === 'processing'}
              className="flex items-center gap-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-500 p-4 text-left transition-colors disabled:opacity-60"
            >
              <CreditCardIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 shrink-0" />
              <div>
                <p className="font-medium text-neutral-900 dark:text-neutral-100 text-sm">Credit / Debit card</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {surchargePercent > 0
                    ? `+${surchargePercent}% processing fee · $${(balance * surchargePercent / 100).toFixed(2)} added at checkout`
                    : 'Processed securely via Stripe'}
                </p>
              </div>
              {paying && <Spinner size="sm" className="ml-auto" />}
            </button>

            {/* ACH bank debit */}
            <button
              onClick={handleAchPay}
              disabled={paying || achState === 'collecting' || achState === 'processing'}
              className="flex items-center gap-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-500 p-4 text-left transition-colors disabled:opacity-60"
            >
              <BuildingLibraryIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 shrink-0" />
              <div>
                <p className="font-medium text-neutral-900 dark:text-neutral-100 text-sm">
                  Bank account (ACH)
                  {surchargePercent > 0 && (
                    <span className="ml-2 text-xs font-semibold text-green-600 dark:text-green-400">No fee</span>
                  )}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {achState === 'collecting' ? 'Connecting to your bank…'
                    : achState === 'processing' ? 'Confirming payment…'
                    : '1–3 business days to clear'}
                </p>
              </div>
              {(achState === 'collecting' || achState === 'processing') && <Spinner size="sm" className="ml-auto" />}
            </button>
          </div>
          {surchargePercent > 0 && (
            <p className="text-xs text-neutral-400 dark:text-neutral-500">
              A {surchargePercent}% processing fee applies to card payments. Pay by bank account to avoid it.
            </p>
          )}
        </div>
      )}

      {/* Line items */}
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Services</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-400 uppercase tracking-wide border-b border-neutral-200 dark:border-neutral-700">
              <th className="pb-2 pr-4">Description</th>
              <th className="pb-2 pr-4 text-right">Qty</th>
              <th className="pb-2 pr-4 text-right">Unit price</th>
              <th className="pb-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {invoice.items?.map((item) => (
              <tr key={item.id}>
                <td className="py-2 pr-4 text-neutral-900 dark:text-neutral-100">{item.description}</td>
                <td className="py-2 pr-4 text-right text-neutral-700 dark:text-neutral-300">{item.quantity}</td>
                <td className="py-2 pr-4 text-right text-neutral-700 dark:text-neutral-300">${parseFloat(item.unitPrice).toFixed(2)}</td>
                <td className="py-2 text-right text-neutral-900 dark:text-neutral-100">${parseFloat(item.total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-1 text-sm text-right">
          <div className="flex justify-end gap-12 text-neutral-700 dark:text-neutral-300">
            <span>Subtotal</span><span>${parseFloat(invoice.subtotal).toFixed(2)}</span>
          </div>
          {parseFloat(invoice.tax) > 0 && (
            <div className="flex justify-end gap-12 text-neutral-700 dark:text-neutral-300">
              <span>Tax</span><span>${parseFloat(invoice.tax).toFixed(2)}</span>
            </div>
          )}
          {parseFloat(invoice.discount) > 0 && (
            <div className="flex justify-end gap-12 text-neutral-700 dark:text-neutral-300">
              <span>Discount</span><span>-${parseFloat(invoice.discount).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-end gap-12 font-bold text-neutral-900 dark:text-neutral-100 text-base pt-1">
            <span>Total</span><span>${parseFloat(invoice.total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment history */}
      {invoice.payments?.length > 0 && (
        <div className="card">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Payments received</h2>
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {invoice.payments.map((pmt) => (
              <li key={pmt.id} className="py-3 flex justify-between text-sm">
                <div>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{format(new Date(pmt.paymentDate), 'MMM d, yyyy')}</p>
                  {pmt.paymentMethod && (
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5 capitalize">{pmt.paymentMethod.replace(/_/g, ' ')}</p>
                  )}
                </div>
                <span className="font-medium text-green-600 dark:text-green-400">+${parseFloat(pmt.amount).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
